import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, ShieldAlert, Loader2, Download, Eye, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { fmtTz } from "../lib/time";

const PPE_OPTIONS = [
  { id: 'hard_hat', label: 'Hard Hat' },
  { id: 'safety_glasses', label: 'Safety Glasses' },
  { id: 'hi_vis_vest', label: 'Hi-Vis Vest' },
  { id: 'steel_toe_boots', label: 'Steel-Toed Boots' },
  { id: 'gloves', label: 'Gloves' },
  { id: 'hearing_protection', label: 'Hearing Protection' },
  { id: 'respirator', label: 'Respirator / Dust Mask' },
  { id: 'harness', label: 'Fall Protection Harness' },
  { id: 'face_shield', label: 'Face Shield' },
];

const RISK_COLORS = {
  low: 'bg-emerald-100 text-emerald-700',
  medium: 'bg-amber-100 text-amber-700',
  high: 'bg-rose-100 text-rose-700',
};

export default function AdminFLRA() {
  const { authAxios, user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const [formData, setFormData] = useState({
    date: '',
    project_id: '',
    site: '',
    task_description: '',
    crew_members: '',
    emergency_procedures: '',
    nearest_hospital: '',
    worker_signature: '',
    supervisor_signature: '',
    ppe_required: [],
    hazards: [{ hazard: '', risk_level: 'low', controls: '' }],
  });

  const fetchData = useCallback(async () => {
    try {
      const [res, projRes] = await Promise.all([
        authAxios.get('/forms/flra'),
        authAxios.get('/projects'),
      ]);
      setItems(res.data);
      setProjects(projRes.data);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, [authAxios]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetForm = () => setFormData({
    date: '', project_id: '', site: '', task_description: '', crew_members: '',
    emergency_procedures: '', nearest_hospital: '', worker_signature: '', supervisor_signature: '',
    ppe_required: [], hazards: [{ hazard: '', risk_level: 'low', controls: '' }],
  });

  const togglePPE = (id) => {
    setFormData((prev) => ({
      ...prev,
      ppe_required: prev.ppe_required.includes(id)
        ? prev.ppe_required.filter((p) => p !== id)
        : [...prev.ppe_required, id],
    }));
  };

  const addHazard = () => setFormData({ ...formData, hazards: [...formData.hazards, { hazard: '', risk_level: 'low', controls: '' }] });
  const removeHazard = (i) => setFormData({ ...formData, hazards: formData.hazards.filter((_, idx) => idx !== i) });
  const updateHazard = (i, field, val) => {
    const arr = [...formData.hazards];
    arr[i] = { ...arr[i], [field]: val };
    setFormData({ ...formData, hazards: arr });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.date || !formData.site || !formData.task_description || !formData.crew_members || !formData.emergency_procedures || !formData.worker_signature) {
      toast.error('Please fill all required fields');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        project_id: formData.project_id && formData.project_id !== 'none' ? formData.project_id : null,
        supervisor_signature: formData.supervisor_signature || null,
        nearest_hospital: formData.nearest_hospital || null,
        hazards: formData.hazards.filter(h => h.hazard.trim()),
      };
      await authAxios.post('/forms/flra', payload);
      toast.success('FLRA submitted');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const exportPDF = async (id) => {
    try {
      const res = await authAxios.get(`/pdf/flra/${id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `flra_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded');
    } catch { toast.error('Failed to export PDF'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this FLRA?')) return;
    try {
      await authAxios.delete(`/forms/flra/${id}`);
      toast.success('Deleted');
      fetchData();
    } catch { toast.error('Failed to delete'); }
  };

  const highestRisk = (hazards) => {
    if (!hazards?.length) return null;
    if (hazards.some(h => h.risk_level === 'high')) return 'high';
    if (hazards.some(h => h.risk_level === 'medium')) return 'medium';
    return 'low';
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="flra-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-stone-900 font-heading">Field Level Risk Assessment</h1>
          <p className="text-stone-500 mt-1">Identify hazards and controls before starting a task</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="new-flra-btn">
              <Plus className="w-5 h-5 mr-2" />New FLRA
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ShieldAlert className="w-5 h-5 text-amber-600" />Field Level Risk Assessment
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-4">
                <h3 className="font-semibold text-stone-700 border-b pb-2">Assessment Info</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Date *</Label>
                    <Input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} required data-testid="flra-date" />
                  </div>
                  <div className="space-y-2">
                    <Label>Project (optional)</Label>
                    <Select value={formData.project_id} onValueChange={(v) => setFormData({ ...formData, project_id: v })}>
                      <SelectTrigger data-testid="flra-project"><SelectValue placeholder="Select project" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Project</SelectItem>
                        {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Site / Location *</Label>
                  <Input value={formData.site} onChange={(e) => setFormData({ ...formData, site: e.target.value })} required data-testid="flra-site" />
                </div>
                <div className="space-y-2">
                  <Label>Task Description *</Label>
                  <Textarea value={formData.task_description} onChange={(e) => setFormData({ ...formData, task_description: e.target.value })} rows={2} placeholder="Describe the task being assessed" required data-testid="flra-task" />
                </div>
                <div className="space-y-2">
                  <Label>Crew Members *</Label>
                  <Textarea value={formData.crew_members} onChange={(e) => setFormData({ ...formData, crew_members: e.target.value })} rows={2} placeholder="List everyone involved in this task" required data-testid="flra-crew" />
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between border-b pb-2">
                  <h3 className="font-semibold text-stone-700">Hazard Identification</h3>
                  <Button type="button" variant="outline" size="sm" onClick={addHazard} data-testid="add-hazard-btn">
                    <Plus className="w-4 h-4 mr-1" />Add Hazard
                  </Button>
                </div>
                {formData.hazards.map((h, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-end p-3 bg-stone-50 rounded-lg">
                    <div className="col-span-4 space-y-1">
                      <Label className="text-xs">Hazard</Label>
                      <Input value={h.hazard} onChange={(e) => updateHazard(i, 'hazard', e.target.value)} placeholder="e.g. Working at height" data-testid={`hazard-${i}`} />
                    </div>
                    <div className="col-span-2 space-y-1">
                      <Label className="text-xs">Risk</Label>
                      <Select value={h.risk_level} onValueChange={(v) => updateHazard(i, 'risk_level', v)}>
                        <SelectTrigger data-testid={`risk-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-5 space-y-1">
                      <Label className="text-xs">Controls</Label>
                      <Input value={h.controls} onChange={(e) => updateHazard(i, 'controls', e.target.value)} placeholder="e.g. Harness + tie-off" data-testid={`controls-${i}`} />
                    </div>
                    <div className="col-span-1">
                      {formData.hazards.length > 1 && (
                        <Button type="button" variant="ghost" size="icon" onClick={() => removeHazard(i)}>
                          <X className="w-4 h-4 text-rose-600" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="space-y-3">
                <h3 className="font-semibold text-stone-700 border-b pb-2">PPE Required</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {PPE_OPTIONS.map((p) => (
                    <label key={p.id} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox checked={formData.ppe_required.includes(p.id)} onCheckedChange={() => togglePPE(p.id)} data-testid={`ppe-${p.id}`} />
                      <span className="text-sm">{p.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-stone-700 border-b pb-2">Emergency Procedures</h3>
                <div className="space-y-2">
                  <Label>Procedures *</Label>
                  <Textarea value={formData.emergency_procedures} onChange={(e) => setFormData({ ...formData, emergency_procedures: e.target.value })} rows={3} placeholder="Muster point, who to call, first aid location..." required data-testid="flra-emergency" />
                </div>
                <div className="space-y-2">
                  <Label>Nearest Hospital</Label>
                  <Input value={formData.nearest_hospital} onChange={(e) => setFormData({ ...formData, nearest_hospital: e.target.value })} placeholder="Name and address" />
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-stone-700 border-b pb-2">Sign-off</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Worker Signature (type name) *</Label>
                    <Input value={formData.worker_signature} onChange={(e) => setFormData({ ...formData, worker_signature: e.target.value })} required data-testid="flra-worker-sig" />
                  </div>
                  <div className="space-y-2">
                    <Label>Supervisor Signature (optional)</Label>
                    <Input value={formData.supervisor_signature} onChange={(e) => setFormData({ ...formData, supervisor_signature: e.target.value })} data-testid="flra-sup-sig" />
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button type="submit" disabled={submitting} className="bg-emerald-600 hover:bg-emerald-500" data-testid="submit-flra-btn">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Submit
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-amber-600" />Records ({items.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-emerald-600" /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-stone-500"><ShieldAlert className="w-12 h-12 mx-auto mb-4 opacity-50" /><p>No FLRA records yet</p></div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Task</TableHead>
                    <TableHead>Site</TableHead>
                    <TableHead>Hazards</TableHead>
                    <TableHead>Highest Risk</TableHead>
                    <TableHead>Submitted By</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((it) => {
                    const risk = highestRisk(it.hazards);
                    return (
                      <TableRow key={it.id}>
                        <TableCell>{fmtTz(it.date, 'MMM d, yyyy')}</TableCell>
                        <TableCell className="font-medium max-w-[200px] truncate">{it.task_description}</TableCell>
                        <TableCell>{it.site}</TableCell>
                        <TableCell><Badge variant="outline">{it.hazards?.length || 0}</Badge></TableCell>
                        <TableCell>{risk ? <Badge className={RISK_COLORS[risk]}>{risk.toUpperCase()}</Badge> : '-'}</TableCell>
                        <TableCell>{it.submitted_by_name}</TableCell>
                        <TableCell className="text-right space-x-1 whitespace-nowrap">
                          <Button variant="ghost" size="icon" onClick={() => { setSelected(it); setViewOpen(true); }} data-testid={`view-flra-${it.id}`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => exportPDF(it.id)} data-testid={`pdf-flra-${it.id}`}>
                            <Download className="w-4 h-4" />
                          </Button>
                          {isAdmin && (
                            <Button variant="ghost" size="icon" onClick={() => handleDelete(it.id)} data-testid={`delete-flra-${it.id}`}>
                              <Trash2 className="w-4 h-4 text-rose-600" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-amber-600" />FLRA Details
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-stone-500">Date</p><p className="font-medium">{fmtTz(selected.date, 'MMM d, yyyy')}</p></div>
                <div><p className="text-stone-500">Site</p><p className="font-medium">{selected.site}</p></div>
                <div><p className="text-stone-500">Project</p><p className="font-medium">{selected.project_name || '-'}</p></div>
                <div><p className="text-stone-500">Submitted By</p><p className="font-medium">{selected.submitted_by_name}</p></div>
              </div>
              <div><p className="text-stone-500">Task</p><p>{selected.task_description}</p></div>
              <div><p className="text-stone-500">Crew Members</p><p className="whitespace-pre-wrap">{selected.crew_members}</p></div>
              {selected.hazards?.length > 0 && (
                <div>
                  <p className="text-stone-500 mb-2">Hazards</p>
                  <div className="space-y-2">
                    {selected.hazards.map((h, i) => (
                      <div key={i} className="p-3 bg-stone-50 rounded-lg">
                        <div className="flex items-center justify-between mb-1">
                          <p className="font-medium">{h.hazard}</p>
                          <Badge className={RISK_COLORS[h.risk_level]}>{h.risk_level.toUpperCase()}</Badge>
                        </div>
                        <p className="text-stone-600 text-xs">Controls: {h.controls || '-'}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {selected.ppe_required?.length > 0 && (
                <div>
                  <p className="text-stone-500 mb-1">PPE Required</p>
                  <div className="flex flex-wrap gap-1">
                    {selected.ppe_required.map((p) => (
                      <Badge key={p} variant="outline">{PPE_OPTIONS.find(x => x.id === p)?.label || p}</Badge>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <p className="text-stone-500">Emergency Procedures</p>
                <p className="whitespace-pre-wrap">{selected.emergency_procedures}</p>
              </div>
              {selected.nearest_hospital && <div><p className="text-stone-500">Nearest Hospital</p><p>{selected.nearest_hospital}</p></div>}
              <div className="pt-4 border-t grid grid-cols-2 gap-4">
                <div><p className="text-stone-500">Worker Signature</p><p className="font-medium italic">{selected.worker_signature}</p></div>
                {selected.supervisor_signature && <div><p className="text-stone-500">Supervisor Signature</p><p className="font-medium italic">{selected.supervisor_signature}</p></div>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
